import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, AlertDialog } from '@/components/shared/ui';
import { useAuth as useAuthApi } from '@/data/queries/useAuth';
import { useUser } from '@/data/queries/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/lib/constants/routes';
import { isValidEmailFormat, normalizeEmailForAuth } from '@/utils/emailValidation';

// Logo is now in public folder
const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

export default function EmailChange(): React.ReactElement {
    const { t } = useTranslation(['auth', 'common']);
    const { checkEmail, sendVerificationEmail, verifyVerificationCode } = useAuthApi();
    const { getProfile } = useUser();
    const { user, updateUserProfile } = useAuth();
    const [email, setEmail] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [emailFieldError, setEmailFieldError] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [resendCooldown, setResendCooldown] = useState<number>(0);
    const [sentToEmailNormalized, setSentToEmailNormalized] = useState<string | null>(null);
    const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string }>({
        isOpen: false,
        message: '',
    });
    const navigate = useNavigate();
    const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

    const currentEmailNormalized = normalizeEmailForAuth(user?.email || '');

    const showAlert = useCallback((message: string) => {
        setAlertState({ isOpen: true, message });
    }, []);

    const closeAlert = useCallback(() => {
        setAlertState({ isOpen: false, message: '' });
    }, []);

    const startResendCooldown = useCallback(() => {
        setResendCooldown(60);

        if (resendTimerRef.current) {
            clearInterval(resendTimerRef.current);
        }

        resendTimerRef.current = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    if (resendTimerRef.current) {
                        clearInterval(resendTimerRef.current);
                        resendTimerRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    const validateEmailField = useCallback((): string | null => {
        const trimmed = email.trim();
        if (!trimmed) {
            return t('auth:emailChange.alertEmailRequired');
        }
        if (!isValidEmailFormat(trimmed)) {
            return t('auth:emailChange.alertEmailInvalid');
        }
        const normalized = normalizeEmailForAuth(trimmed);
        if (currentEmailNormalized && normalized === currentEmailNormalized) {
            return t('auth:emailChange.alertSameEmail');
        }
        return null;
    }, [currentEmailNormalized, email, t]);

    const handleSendCode = async () => {
        const emailErr = validateEmailField();
        if (emailErr) {
            setEmailFieldError(emailErr);
            showAlert(emailErr);
            return;
        }

        setEmailFieldError('');
        const emailNormalized = normalizeEmailForAuth(email);
        setIsSending(true);

        try {
            const check = await checkEmail(emailNormalized);
            if (!check.isAvailable) {
                showAlert(check.message || t('auth:emailChange.alertEmailInUse'));
                return;
            }

            await sendVerificationEmail(emailNormalized);
            setSentToEmailNormalized(emailNormalized);
            setCode('');
            showAlert(t('auth:emailChange.alertCodeSent'));
            startResendCooldown();
        } catch (error: unknown) {
            showAlert(error instanceof Error && error.message ? error.message : t('auth:emailChange.alertCodeSendError'));
        } finally {
            setIsSending(false);
        }
    };

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();

        const emailErr = validateEmailField();
        if (emailErr) {
            setEmailFieldError(emailErr);
            showAlert(emailErr);
            return;
        }
        setEmailFieldError('');

        const emailNormalized = normalizeEmailForAuth(email);
        if (!sentToEmailNormalized || emailNormalized !== sentToEmailNormalized) {
            showAlert(t('auth:emailChange.alertEmailMustMatchSent'));
            return;
        }

        if (!code.trim()) {
            showAlert(t('auth:emailChange.alertCodeRequired'));
            return;
        }

        if (code.trim().length !== 6) {
            showAlert(t('auth:emailChange.alertCodeLength'));
            return;
        }

        setIsVerifying(true);

        try {
            await verifyVerificationCode(emailNormalized, code.trim());
            const profile = await getProfile();
            if (profile.email) {
                updateUserProfile({ email: profile.email });
            }
            navigate(ROUTES.AUTH.EMAIL_CHANGE_COMPLETE);
        } catch (error: unknown) {
            showAlert(error instanceof Error && error.message ? error.message : t('auth:emailChange.alertVerifyError'));
        } finally {
            setIsVerifying(false);
        }
    };

    const handleCancel = () => {
        navigate(ROUTES.AUTH.SETTINGS);
    };

    React.useEffect(() => {
        return () => {
            if (resendTimerRef.current) {
                clearInterval(resendTimerRef.current);
            }
        };
    }, []);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-8">
            {/* Email Change Card */}
            <div className="w-full max-w-[460px] bg-white rounded-3xl p-14 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
                {/* Logo */}
                <div className="text-center mb-10">
                    <img src={medicrewLogo} alt="Medicrew" className="h-7 w-auto mx-auto" />
                </div>

                {/* Title */}
                <h1 className="text-2xl font-semibold text-brand-500 text-center mb-4">
                    {t('auth:emailChange.title')}
                </h1>
                <p className="text-center text-gray-600 text-base mb-10">
                    {t('auth:emailChange.description')}
                </p>

                {/* Form */}
                <form onSubmit={handleVerify} className="flex flex-col gap-6">
                    <div>
                        <label htmlFor="email" className="block mb-2 text-sm font-semibold text-gray-900">
                            {t('auth:emailChange.newEmailLabel')}
                        </label>
                        <Input
                            type="email"
                            id="email"
                            autoComplete="email"
                            className={`bg-gray-50 focus:bg-white border-transparent focus:border-brand-500 rounded-xl ${emailFieldError ? 'border-red-500 focus:border-red-500' : ''}`}
                            placeholder="example@email.com"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (emailFieldError) setEmailFieldError('');
                            }}
                            onBlur={() => {
                                const err = validateEmailField();
                                setEmailFieldError(err ?? '');
                            }}
                            required
                        />
                        {emailFieldError ? (
                            <p className="mt-1.5 text-xs font-medium text-red-600">{emailFieldError}</p>
                        ) : (
                            <p className="mt-1.5 text-xs text-gray-500">{t('auth:emailChange.emailHint')}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="code" className="block mb-2 text-sm font-semibold text-gray-900">
                            {t('auth:emailChange.codeLabel')}
                        </label>
                        <div className="flex gap-3">
                            <Input
                                type="text"
                                id="code"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                className="flex-1 bg-gray-50 focus:bg-white border-transparent focus:border-brand-500 rounded-xl"
                                placeholder={t('auth:emailVerification.codePlaceholder')}
                                maxLength={6}
                                value={code}
                                onChange={(e) => {
                                    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setCode(v);
                                }}
                                required
                            />
                            <Button
                                type="button"
                                onClick={handleSendCode}
                                variant="secondary"
                                className="shrink-0 px-6 border-2 border-brand-500 text-brand-500 rounded-xl h-10"
                                disabled={isSending || resendCooldown > 0}
                            >
                                {isSending
                                    ? t('auth:emailChange.sending')
                                    : resendCooldown > 0
                                        ? t('auth:emailChange.resendButton', { seconds: resendCooldown })
                                        : t('auth:emailVerification.sendButton')
                                }
                            </Button>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-6">
                        <Button
                            type="submit"
                            className="flex-1 rounded-full text-lg h-12"
                            variant="primary"
                            disabled={isVerifying}
                        >
                            {isVerifying ? t('auth:emailChange.verifying') : t('common:confirm')}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleCancel}
                            className="flex-1 rounded-full text-lg h-12"
                            variant="secondary"
                        >
                            {t('common:cancel')}
                        </Button>
                    </div>
                </form>

                {/* Help Section */}
                <div className="mt-12 p-6 bg-gray-50 rounded-2xl">
                    <h2 className="text-sm font-bold text-gray-900 mb-4">
                        {t('auth:emailVerification.helpTitle')}
                    </h2>
                    <ul className="text-xs text-gray-500 space-y-2 list-none p-0">
                        <li className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-gray-400">
                            {t('auth:emailVerification.noteDelay')}
                        </li>
                        <li className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-gray-400">
                            {t('auth:emailVerification.noteSpam')}
                        </li>
                        <li className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-gray-400">
                            {t('auth:emailVerification.noteFirewall')}
                        </li>
                    </ul>
                </div>
            </div>
            <AlertDialog
                isOpen={alertState.isOpen}
                onClose={closeAlert}
                message={alertState.message}
            />
        </div>
    );
}
